/**
 * ProgressiveImage -- Smooth image loading for chat messages
 *
 * Provides a shimmer placeholder while loading, optional low-res thumbnail,
 * CSS fade-in on load, IntersectionObserver lazy loading, and error state.
 * No external image libraries required (no blurhash).
 */

import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';

interface ProgressiveImageProps {
  src: string;
  alt?: string;
  /** Optional low-res thumbnail shown while the full image loads */
  thumbnail?: string | null;
  width?: number;
  height?: number;
  className?: string;
  onClick?: () => void;
}

/**
 * CSS keyframes injected once for the shimmer animation.
 * Uses a moving linear-gradient to create a pulsing/shimmer effect.
 */
const SHIMMER_STYLE_ID = 'progressive-image-shimmer';

function ensureShimmerStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SHIMMER_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = SHIMMER_STYLE_ID;
  style.textContent = `
    @keyframes progressive-image-shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
}

export const ProgressiveImage = memo(function ProgressiveImage({
  src,
  alt = '',
  thumbnail = null,
  width,
  height,
  className = '',
  onClick,
}: ProgressiveImageProps) {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Inject shimmer keyframes once
  useEffect(() => {
    ensureShimmerStyle();
  }, []);

  // IntersectionObserver for lazy loading — trigger when within 200px of viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setHasError(false);
  }, []);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoaded(false);
  }, []);

  // Aspect ratio style for maintaining dimensions before load
  const aspectStyle: React.CSSProperties =
    width && height ? { aspectRatio: `${width} / ${height}` } : {};

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-lg ${className}`}
      style={{
        ...aspectStyle,
        cursor: onClick ? 'pointer' : undefined,
      }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Shimmer placeholder — visible while image has not loaded */}
      {!isLoaded && !hasError && (
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, var(--color-neutral-50) 25%, var(--color-neutral-100) 50%, var(--color-neutral-50) 75%)',
            backgroundSize: '200% 100%',
            animation: 'progressive-image-shimmer 1.5s ease-in-out infinite',
          }}
          aria-hidden="true"
        />
      )}

      {/* Low-res thumbnail — shown blurred behind the full image */}
      {thumbnail && !hasError && (
        <img
          src={thumbnail}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          style={{
            filter: 'blur(20px)',
            transform: 'scale(1.1)', // Prevent blur edge artifacts
            opacity: isLoaded ? 0 : 1,
            transition: 'opacity 0.3s ease-out',
          }}
        />
      )}

      {/* Full-resolution image — fades in when loaded */}
      {isInView && !hasError && (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          className="relative block h-full w-full object-cover"
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.4s ease-out',
          }}
          draggable={false}
        />
      )}

      {/* Error state — broken image icon */}
      {hasError && (
        <div className="flex h-full min-h-[8rem] w-full flex-col items-center justify-center gap-2 bg-white/30 text-neutral-800/40">
          <svg
            className="h-10 w-10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* Broken image icon */}
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="3" y1="15" x2="8.5" y2="10" />
            <line x1="8.5" y1="10" x2="12" y2="13" />
            <line x1="12" y1="13" x2="15.5" y2="9.5" />
            <line x1="15.5" y1="9.5" x2="21" y2="15" />
            {/* X mark across */}
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <span className="text-xs font-medium">{t('common.imageUnavailable')}</span>
        </div>
      )}
    </div>
  );
});

export default ProgressiveImage;
